import {
  ApolloError,
  DocumentNode,
  NoInfer,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client';
import { useToast } from '../components/Toast/ToastContainer';

interface GQLMutationConfiguration<K> {
  onError?: (error: ApolloError) => void;
  onCompleted?: (data: NoInfer<K>) => void;
}

export const useGQLMutation = <K, T>(
  GQL: DocumentNode,
  configuration: GQLMutationConfiguration<K>
) => {
  const { setToast } = useToast();
  const [mutation, { error, data, loading }] = useMutation<K, T>(GQL, {
    onError: (err: any) => {
      setToast({ content: err?.message, type: 'error' });
      configuration?.onError ? configuration?.onError(err) : (() => {})();
      if (err?.graphQLErrors?.[0]?.extensions?.code === 'UNAUTHENTICATED') {
        window.location.replace('/');
      }
    },
    onCompleted: (d: any) => {
      configuration?.onCompleted((d as any)?.[Object.keys(d)[0]] as K);
    },
  });

  return {
    mutation,
    error,
    data: (data as any)?.[Object.keys(data)[0]] as K,
    loading,
  };
};

export const useGQLLazyQuery = <K, T>(
  GQL: DocumentNode,
  configuration: GQLMutationConfiguration<K> = {
    onCompleted: () => {},
    onError: () => {},
  }
) => {
  const { setToast } = useToast();
  const [lazy, { data, loading, error }] = useLazyQuery<K, T>(GQL, {
    onError: (err: any) => {
      setToast({ content: err?.message, type: 'error' });
      configuration?.onError(err);
      if (err?.graphQLErrors[0]?.extensions?.code === 'UNAUTHENTICATED') {
        window.location.replace('/');
      }
    },
    onCompleted: (d: any) => {
      configuration?.onCompleted((d as any)?.[Object.keys(d)[0]] as K);
    },
  });

  return {
    lazy,
    data: (data as any)?.[Object.keys(data)[0]] as K,
    loading,
    error,
  };
};

export const useGQLQuery = <K, T>(
  GQL: DocumentNode,
  variables: T = undefined,
  configuration: GQLMutationConfiguration<K> = {
    onCompleted: () => {},
    onError: () => {},
  }
) => {
  const { setToast } = useToast();
  const { data, loading, error } = useQuery<K, T>(GQL, {
    onError: (err: any) => {
      setToast({ content: err?.message, type: 'error' });
      configuration?.onError(err);
      if (err.graphQLErrors[0].extensions.code === 'UNAUTHENTICATED') {
        window.location.replace('/');
      }
    },
    onCompleted: (d: any) => {
      configuration?.onCompleted((d as any)?.[Object.keys(d)[0]] as K);
    },
    variables,
  });

  return {
    data: (data as any)?.[Object.keys(data)[0]] as K,
    loading,
    error,
  };
};
